import React, { useState, useRef } from 'react'
import { Box, Button, Card, CardContent, Chip, Dialog, DialogContent, DialogTitle, IconButton, TextField, Typography } from '@mui/material';
import { CameraAlt, PhotoCamera, Close as CloseIcon, Image as ImageIcon } from '@mui/icons-material';
import { ILecturaAgua } from '../models/lecturas'
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { updateItem } from '../redux/readSlice';
import { useLecturas } from '../hooks/useLecturas';
import { CameraComponent } from './CameraComponent';
import { ThermalPrinter, thermalPrinterEncoder } from '../utils/printer';

interface LecturaAguaItemProps {
    lectura: ILecturaAgua
}

export const LecturaAguaItem = (props: LecturaAguaItemProps) => {

    const { lectura } = props

    const { sendLectura, uploadFoto } = useLecturas()

    const { nombre_Empresa } = useAppSelector((state) =>  state.auth.userData!)
    const { lect_Actual, lect_Anterior, limite_M3, num_Casa, agua_derecho } = lectura

    // Si el servidor ya trae una lectura actual distinta de 0 y de la anterior, la casa ya fue leida
    const yaGuardada = !!lect_Actual && lect_Actual !== lect_Anterior
    const estado = lectura.estado ?? (yaGuardada ? 'Guardado' : undefined)
    const fotoGuardada = lectura.foto && lectura.foto.trim() !== '' ? lectura.foto : null

    const [lecturaActual, setLecturaActual] = useState(lect_Actual)
    const [consumo, setConsumo] = useState(yaGuardada ? lect_Actual - lect_Anterior : -1)
    const [verFoto, setVerFoto] = useState(false)
    const [fotoError, setFotoError] = useState(false)
    const [foto, setFoto] = useState<File | null>(null)
    const [fotoPreview, setFotoPreview] = useState<string | null>(null)
    const [guardando, setGuardando] = useState(false)
    const [showCamera, setShowCamera] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const dispatch = useAppDispatch()

    const handleCameraCapture = (capturedFile: File) => {
        setFoto(capturedFile);
        setFotoPreview(URL.createObjectURL(capturedFile));
        setShowCamera(false);
    };

    const handleGallerySelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setFoto(file);
            setFotoPreview(URL.createObjectURL(file));
        }
    };

    const handleRemoveFoto = () => {
        setFoto(null);
        if (fotoPreview) URL.revokeObjectURL(fotoPreview);
        setFotoPreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleGuardar = async () => {

        const consumoTemp = lecturaActual - lect_Anterior

        if(consumoTemp < 0){
            alert('La lectura actual no puede ser menor a la anterior')
            return
        }
        else if(consumoTemp > limite_M3){
            if(!window.confirm(`El consumo es de ${consumoTemp.toFixed(2)} m3, y excede el limite de ${limite_M3} m3. ¿Esta seguro de guardar el dato?`)){
                return
            }
        }
        else if(consumoTemp==0){
            if(!window.confirm('La lectura actual es igual a la anterior. ¿Esta seguro de guardar el dato?')){
                return
            }
        }
        setConsumo(consumoTemp)
        setGuardando(true)

        let fotoUrl: string | undefined;
        if (foto && navigator.onLine) {
            fotoUrl = (await uploadFoto(lectura.empresa, foto)) || undefined;
        }

        const req = await sendLectura({
            ...lectura,
            lect_Actual: lecturaActual
        }, fotoUrl)

        setGuardando(false)

        if (req == 'offline') {
            dispatch(updateItem({
                ...lectura,
                lect_Actual: lecturaActual,
                estado: 'Guardado Offline'
            }))
        }
        else if (req && req.success){
            dispatch(updateItem({
                ...lectura,
                lect_Actual: lecturaActual,
                estado: 'Guardado',
                foto: fotoUrl || lectura.foto
            }))
            if (fotoUrl) handleRemoveFoto()
        }
        else{
            alert('No se pudo guardar la lectura')
        }
    }

    const handlePrint = async () => {
        
        let data = thermalPrinterEncoder
        .initialize()
        .height(2)
        .line('        Lectura de Agua      ')
        .line(`       ${nombre_Empresa}     `)
        .newline()
        .align('left')
        .line(`Casa:              ${num_Casa}`)
        .line(`Fecha:             ${new Date().toLocaleDateString()}`)
        .line(`Hora:              ${new Date().toLocaleTimeString([],{hour: '2-digit', minute:'2-digit'})}`)
        .line(`Lectura Anterior:  ${lect_Anterior}`)
        .line(`Lectura Actual:    ${lecturaActual}`)
        .line(`Consumo:           ${consumo.toFixed(2)} m3`)
        .line(`Exceso:            ${consumo>agua_derecho? (consumo-agua_derecho).toFixed(2):0} m3`)
        .encode()

        ThermalPrinter(data)
    }

    return (
        <Card  
            sx={{
                width: { xs: '70%', md: '30%' },
                margin: '1em',
                padding: '2em',
            }}
        >
            <CardContent>
                
                {estado === undefined && <Chip label="Sin guardar" color="error" size="small" sx={{alignSelf:'end',minWidth:'30%'}}/>}
                {estado === 'Guardado' && <Chip label="Guardado" color="success" size="small" sx={{alignSelf:'end',minWidth:'30%'}}/>}
                {estado === 'Guardado Offline' && <Chip label="Guardado Offline" color="warning" size="small" sx={{alignSelf:'end',minWidth:'30%'}}/>}

                <Typography sx={{ my: 1.5 }} color="text.secondary">
                    Casa {num_Casa}
                </Typography>

                <Typography variant="h6" component="div">
                    Lectura Anterior: {lect_Anterior}
                </Typography>

                <TextField fullWidth required type='number' defaultValue={lecturaActual} label="Lectura Actual" variant="outlined"
                    sx={{marginY:2}}
                    onChange={(e) => setLecturaActual(parseFloat(e.target.value))}
                    InputProps={{
                        inputProps: {
                            min: 0,
                        }
                    }}
                />

                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleGallerySelect}
                    style={{ display: 'none' }}
                />

                {!fotoPreview && fotoGuardada && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, p: 1, borderRadius: 1, border: '1px solid #e0e0e0' }}>
                        <Box
                            component="img"
                            src={fotoGuardada}
                            alt={'Lectura casa ' + num_Casa}
                            onClick={() => { setFotoError(false); setVerFoto(true) }}
                            sx={{ width: 56, height: 56, borderRadius: 1, objectFit: 'cover', border: '1px solid #e0e0e0', cursor: 'pointer' }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, textAlign: 'left' }}>Foto guardada</Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ImageIcon />}
                            onClick={() => { setFotoError(false); setVerFoto(true) }}
                            sx={{ textTransform: 'none' }}
                        >
                            Ver foto
                        </Button>
                    </Box>
                )}

                {fotoPreview ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, p: 1, borderRadius: 1, border: '1px solid', borderColor: 'success.main', bgcolor: 'rgba(46,125,50,0.06)' }}>
                        <Box
                            component="img"
                            src={fotoPreview}
                            sx={{ width: 56, height: 56, borderRadius: 1, objectFit: 'cover', border: '1px solid #e0e0e0' }}
                        />
                        <Typography variant="caption" color="success.dark" sx={{ flex: 1 }}>Foto lista</Typography>
                        <IconButton size="small" onClick={handleRemoveFoto} sx={{ color: 'error.main' }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<CameraAlt />}
                            onClick={() => setShowCamera(true)}
                            sx={{ flex: 1, textTransform: 'none' }}
                        >
                            Cámara
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<PhotoCamera />}
                            onClick={() => fileInputRef.current?.click()}
                            sx={{ flex: 1, textTransform: 'none' }}
                        >
                            Galería
                        </Button>
                    </Box>
                )}

                <Button
                    color='secondary'
                    variant="contained"
                    sx={{ width: '100%', mb: 1 }}
                    onClick={handleGuardar}
                    disabled={guardando}
                >
                    {guardando ? 'Guardando...' : 'Guardar'}
                </Button>

                {showCamera && (
                    <CameraComponent
                        isOpen={showCamera}
                        onClose={() => setShowCamera(false)}
                        onCapture={handleCameraCapture}
                    />
                )}

                {
                    (estado === 'Guardado' || estado === 'Guardado Offline') &&
                    <Button color='info' variant="contained" sx={{marginY:2 , width:'40%'}} onClick={handlePrint}>
                        Imprimir Recibo
                    </Button>
                }

                {consumo >= 0 &&
                    <Typography variant="h6" sx={{ mt: 1.5 }} component="div">
                        Consumo: {consumo.toFixed(2)} m3
                    </Typography>
                }

            </CardContent>

            <Dialog open={verFoto} onClose={() => setVerFoto(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Foto lectura casa {num_Casa}
                    <IconButton aria-label="cerrar" onClick={() => setVerFoto(false)} size="small">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                    {fotoError || !fotoGuardada ? (
                        <Typography variant="body2" color="text.secondary">No se pudo cargar la foto.</Typography>
                    ) : (
                        <img
                            src={fotoGuardada}
                            alt={'Lectura casa ' + num_Casa}
                            style={{ maxWidth: '100%', maxHeight: 420, borderRadius: 8 }}
                            onError={() => setFotoError(true)}
                        />
                    )}
                </DialogContent>
            </Dialog>

        </Card>
    )
}
